// The Agent Hub, `/agents` (spec 14 §4.2, D-512): one card per agent the reader
// may see, the 24 h guardrail banner and the Guardrails tab. A holder of
// `agents.invoke` without `agents.read` reads its own domain's agents only,
// with no config controls (D-512 ruling 8); the server narrows the list.
//
// States: a direct visit with no agent row renders the standard empty state
// (the nav shows no section, D-529 ruling 1); loading shows card skeletons; an
// error over a list already shown is a toast and a stale badge, and with
// nothing shown yet it is the error card.

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { StatusChip } from "../../../../components/status-chip";
import {
  CardSkeleton,
  EmptyState,
  ErrorState,
  ForbiddenState,
} from "../../../../components/states";
import { Tabs } from "../../../../components/tabs";
import { Banner, toast } from "../../../../components/toast";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { AgentCard } from "../components/agent-card";
import { GuardrailsTab, type GuardrailFilters } from "../components/guardrails-tab";
import { useAgents, useGuardrailBanner } from "../hooks/agents-queries";
import { useAgentsPermissions } from "../hooks/permissions";
import { BANNER_GUARDRAIL_KINDS, GUARDRAIL_KINDS, type GuardrailKind } from "../lib/agents-lib";

export type HubTab = "agents" | "guardrails";

export interface HubSearch extends GuardrailFilters {
  readonly tab?: HubTab | undefined;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/u;

/** The Hub's URL state; anything malformed reads as absent. */
export function validateHubSearch(raw: Record<string, unknown>): HubSearch {
  const kind = (GUARDRAIL_KINDS as readonly string[]).includes(String(raw["kind"]))
    ? (raw["kind"] as GuardrailKind)
    : undefined;
  const str = (key: string, re: RegExp) =>
    typeof raw[key] === "string" && re.test(raw[key]) ? raw[key] : undefined;
  return {
    tab: raw["tab"] === "guardrails" ? "guardrails" : undefined,
    kind,
    agent: str("agent", UUID_RE),
    from: str("from", DATE_RE),
    to: str("to", DATE_RE),
  };
}

const DAY_MS = 24 * 60 * 60 * 1000;

function GuardrailBanner({ onReview }: { onReview: () => void }) {
  const { t } = useTranslation();
  // Fixed per mount, so the query key holds still while the screen is open.
  const [from] = useState(() => new Date(Date.now() - DAY_MS).toISOString());
  const events = useGuardrailBanner(from, BANNER_GUARDRAIL_KINDS, true);
  const items = events.data ?? [];
  const injection = items.filter((e) => e.kind === "prompt_injection_suspected").length;
  const spend = items.filter((e) => e.kind === "cap_spend_exceeded").length;
  if (injection === 0 && spend === 0) return null;
  return (
    <Banner kind="warn" data-testid="agents-guardrail-banner">
      <span className="ag-stack">
        {injection > 0 ? (
          <span>{t("agents.hub.banner.injection", { count: injection })}</span>
        ) : null}
        {spend > 0 ? <span>{t("agents.hub.banner.spend", { count: spend })}</span> : null}
        <span>
          <button type="button" className="ag-link" onClick={onReview}>
            {t("agents.hub.banner.review")}
          </button>
        </span>
      </span>
    </Banner>
  );
}

export function AgentsHubScreen({
  search,
  onSearch,
}: {
  search: HubSearch;
  onSearch: (next: HubSearch) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const perms = useAgentsPermissions();
  const agents = useAgents(perms.canList);
  const tab: HubTab =
    search.tab === "guardrails" && perms.canReadActivity ? "guardrails" : "agents";

  useEffect(() => {
    if (agents.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [agents.error, navigate]);

  // A refetch that fails over rows already on the glass: say so once, keep the
  // rows, and mark them stale (spec 14 §4.2's error state).
  const toasted = useRef(false);
  const staleError = agents.isError && agents.data !== undefined;
  useEffect(() => {
    if (staleError && !toasted.current) {
      toasted.current = true;
      toast.error(t("agents.hub.loadError"));
    }
    if (!staleError) toasted.current = false;
  }, [staleError, t]);

  if (!perms.canList) {
    return (
      <ForbiddenState
        title={t("agents.common.forbiddenTitle")}
        body={t("agents.common.forbiddenBody")}
      />
    );
  }

  const header = (
    <PageHeader
      title={t("agents.hub.title")}
      lede={t("agents.hub.lede")}
      meta={
        staleError ? (
          <StatusChip variant="warn" data-testid="agents-stale">
            {t("agents.common.stale")}
          </StatusChip>
        ) : undefined
      }
      data-testid="agents-hub-head"
    />
  );

  const tabs = perms.canReadActivity ? (
    <Tabs
      label={t("agents.hub.tabs.aria")}
      active={tab}
      onSelect={(id) => {
        onSearch({ ...search, tab: id === "guardrails" ? "guardrails" : undefined });
      }}
      tabs={[
        { id: "agents", label: t("agents.hub.tabs.agents"), "data-testid": "agents-tab-agents" },
        {
          id: "guardrails",
          label: t("agents.hub.tabs.guardrails"),
          "data-testid": "agents-tab-guardrails",
        },
      ]}
    />
  ) : null;

  let body;
  if (agents.isPending) {
    body = (
      <div className="ag-cards" data-testid="agents-hub-loading">
        <CardSkeleton lines={5} label={t("agents.hub.loading")} />
        <CardSkeleton lines={5} label={t("agents.hub.loading")} />
      </div>
    );
  } else if (agents.data === undefined) {
    body = (
      <ErrorState
        title={t("agents.hub.loadError")}
        retryLabel={t("agents.common.retry")}
        onRetry={() => {
          void agents.refetch();
        }}
        requestId={requestIdOf(agents.error)}
        requestIdLabel={t("agents.common.requestId")}
      />
    );
  } else if (agents.data.length === 0) {
    body = <EmptyState title={t("agents.hub.emptyTitle")} body={t("agents.hub.emptyBody")} />;
  } else if (tab === "guardrails") {
    body = (
      <GuardrailsTab
        agents={agents.data}
        filters={search}
        onFilters={(next) => {
          onSearch({ tab: "guardrails", ...next });
        }}
      />
    );
  } else {
    body = (
      <div className="ag-cards" data-testid="agents-hub-cards">
        {agents.data.map((agent) => (
          <AgentCard
            key={agent.id}
            agent={agent}
            canManage={perms.canManage}
            canConfigure={perms.canRead && perms.canManage}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="ag-stack" data-testid="agents-hub">
      {header}
      {perms.canReadActivity && agents.data !== undefined && agents.data.length > 0 ? (
        <GuardrailBanner
          onReview={() => {
            onSearch({ tab: "guardrails" });
          }}
        />
      ) : null}
      {agents.data !== undefined && agents.data.length > 0 ? tabs : null}
      {body}
      {!perms.canRead && agents.data !== undefined && agents.data.length > 0 ? (
        <p className="ag-hint" data-testid="agents-read-only">
          {t("agents.hub.readOnly")} ·{" "}
          <Link to="/agents/journal" className="ag-link">
            {t("agents.hub.viewJournal")}
          </Link>
        </p>
      ) : null}
    </div>
  );
}
